<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { NIcon, useDialog } from 'naive-ui';
import { IconCheck, IconGrid, IconPlay, IconSettings } from '@/components/design/icons';
import { PlaybookActionType, type PlaybookInDb, type ReviewProjectAnalysisInDb } from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaAnalysisTab from '@/components/design/review/GaiaAnalysisTab.vue';
import GaiaWorkspacePane from '@/components/design/review/GaiaWorkspacePane.vue';
import ReviewAnalysisDataTable from '@/components/reviewProject/ReviewAnalysisDataTable.vue';
import ReviewAnalysisPlaybookPicker from '@/components/reviewProject/ReviewAnalysisPlaybookPicker.vue';
import ReviewAnalysisSettings from '@/components/reviewProject/ReviewAnalysisSettings.vue';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';
import GaiaInlineEdit from '@/components/design/base/inputs/GaiaInlineEdit.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  hasVaultFileDragData,
  readVaultFileDragData,
  type VaultFileDragEntry,
} from '@/utils/vaultFileDrag';

/**
 * The analysis pane of the review workspace (Review Projects mock): one tab per
 * analysis, the evaluation grid of the active one underneath. Files reach an
 * analysis either by being dragged onto its tab or through "Run analysis",
 * which starts runs for whatever is selected in the data room.
 */
const props = defineProps<{
  projectUid: string;
}>();

const reviewProjectStore = useReviewProjectStore();
const dialog = useDialog();
const message = useGaiaToast();

const loading = ref<boolean>(false);
// Rename state may stay local: closing the pane just cancels an in-progress rename.
const renamingUid = ref<string | null>(null);
// Unsaved playbook picks per analysis; local for the same reason as renames.
const playbookDrafts = ref<Record<string, string | null>>({});
// Bumped after dropped files start runs, so the analysis's table reloads.
const refreshTokens = ref<Record<string, number>>({});
// Analysis currently hovered by a file drag (highlights its tab).
const dragOverUid = ref<string | null>(null);
// A run batch is in flight, so "Run analysis" can't be fired twice.
const running = ref<boolean>(false);

const analyses = computed(() => reviewProjectStore.analyses ?? []);

const activeAnalysis = computed<ReviewProjectAnalysisInDb | null>(
  () => analyses.value.find(analysis => analysis.uid === reviewProjectStore.analysisTabUid) ?? null
);

const usedPlaybookUids = computed<string[]>(() =>
  analyses.value.map(analysis => analysis.playbook_uid).filter((uid): uid is string => Boolean(uid))
);

function ensureActiveTab(): void {
  if (!analyses.value.some(analysis => analysis.uid === reviewProjectStore.analysisTabUid)) {
    reviewProjectStore.analysisTabUid = analyses.value[0]?.uid ?? null;
  }
}

onMounted(async () => {
  // Already fetched by a previous mount of this pane (store survives remounts).
  if (reviewProjectStore.analyses !== null) return;
  loading.value = true;
  try {
    reviewProjectStore.analyses = await ReviewProjectService.listAnalyses(props.projectUid);
    ensureActiveTab();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to fetch analyses.');
  } finally {
    loading.value = false;
  }
});

/* ------------------------------------------------------- add / remove tabs */

async function addAnalysisFromPlaybook(playbook: PlaybookInDb): Promise<void> {
  try {
    const analysis = await ReviewProjectService.createAnalysis(props.projectUid, {
      name: playbook.name,
      playbook_uid: playbook.uid,
    });
    reviewProjectStore.analyses = [...analyses.value, analysis];
    reviewProjectStore.analysisTabUid = analysis.uid;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to create analysis.');
  }
}

function confirmDelete(uid: string): void {
  const analysis = analyses.value.find(a => a.uid === uid);
  if (!analysis) return;
  dialog.warning({
    title: 'Delete analysis',
    content: `Delete "${analysis.name}"? This cannot be undone.`,
    positiveText: 'Delete',
    negativeText: 'Cancel',
    onPositiveClick: () => deleteAnalysis(uid),
  });
}

async function deleteAnalysis(uid: string): Promise<void> {
  try {
    await ReviewProjectService.deleteAnalysis(props.projectUid, uid);
    const index = analyses.value.findIndex(analysis => analysis.uid === uid);
    const remaining = analyses.value.filter(analysis => analysis.uid !== uid);
    reviewProjectStore.analyses = remaining;
    reviewProjectStore.analysisSettingsUids = reviewProjectStore.analysisSettingsUids.filter(
      settingsUid => settingsUid !== uid
    );
    delete reviewProjectStore.analysisDataTableState[uid];
    delete playbookDrafts.value[uid];
    if (reviewProjectStore.analysisTabUid === uid) {
      // Keep the position: the right neighbor takes over, or the new last tab.
      reviewProjectStore.analysisTabUid =
        remaining[Math.min(index, remaining.length - 1)]?.uid ?? null;
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to delete analysis.');
  }
}

/* ---------------------------------------------------------------- renaming */

function startRename(analysis: ReviewProjectAnalysisInDb): void {
  renamingUid.value = analysis.uid;
}

/**
 * The field focuses itself, keeps its own draft, guards the double commit that
 * Enter-then-blur used to cause, and only reports a trimmed, changed,
 * non-empty name — so this just saves it. Escape now cancels here too, which
 * it previously did not.
 */
async function commitRename(name: string): Promise<void> {
  const uid = renamingUid.value;
  renamingUid.value = null;
  if (!uid) return;
  try {
    const updated = await ReviewProjectService.updateAnalysis(props.projectUid, uid, { name });
    reviewProjectStore.analyses = analyses.value.map(a => (a.uid === uid ? updated : a));
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to rename analysis.');
  }
}

/* ---------------------------------------------------------------- settings */

function isInSettings(uid: string): boolean {
  return reviewProjectStore.analysisSettingsUids.includes(uid);
}

function openSettings(analysis: ReviewProjectAnalysisInDb): void {
  playbookDrafts.value[analysis.uid] = analysis.playbook_uid ?? null;
  reviewProjectStore.analysisSettingsUids.push(analysis.uid);
}

async function saveSettings(analysis: ReviewProjectAnalysisInDb): Promise<void> {
  // Explicit null detaches the playbook (the backend distinguishes sent-null from omitted).
  const draft = playbookDrafts.value[analysis.uid] ?? null;
  try {
    const updated = await ReviewProjectService.updateAnalysis(props.projectUid, analysis.uid, {
      playbook_uid: draft,
    });
    reviewProjectStore.analyses = analyses.value.map(a => (a.uid === analysis.uid ? updated : a));
    reviewProjectStore.analysisSettingsUids = reviewProjectStore.analysisSettingsUids.filter(
      uid => uid !== analysis.uid
    );
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to save analysis settings.');
  }
}

function handleSettingsToggle(analysis: ReviewProjectAnalysisInDb): void {
  if (isInSettings(analysis.uid)) {
    void saveSettings(analysis);
  } else {
    openSettings(analysis);
  }
}

/* -------------------------------------------------------------- start runs */

async function startRunsForFiles(
  analysis: ReviewProjectAnalysisInDb,
  entries: VaultFileDragEntry[]
): Promise<void> {
  const playbookUid = analysis.playbook_uid;
  if (!playbookUid || running.value) return;

  running.value = true;
  const loadingMsg = message.loading('Starting playbook runs…', { duration: 0 });
  let succeeded = 0;
  const failed: string[] = [];
  try {
    for (const entry of entries) {
      try {
        await PlaybooksService.startRun(playbookUid, {
          input_vault_file_revision_uid: entry.revisionUid,
          review_project_uid: props.projectUid,
          // A review project's analyses evaluate only — no action is executed.
          skip_action_types: Object.values(PlaybookActionType),
        });
        succeeded += 1;
      } catch {
        failed.push(entry.fileName ?? entry.revisionUid);
      }
    }
    if (succeeded) {
      message.success(`Started ${succeeded} playbook run${succeeded > 1 ? 's' : ''}.`);
    }
    if (failed.length) {
      message.error(`Failed to start runs for: ${failed.join(', ')}.`);
    }
  } finally {
    loadingMsg.destroy();
    running.value = false;
    refreshTokens.value[analysis.uid] = (refreshTokens.value[analysis.uid] ?? 0) + 1;
  }
}

// "Run analysis" runs the active analysis over the data-room selection. It is
// the button form of the drag the tabs already accept, so it shares its path.
const selectedRevisionUids = computed(() => reviewProjectStore.dataRoomSelectedRevisionUids);

const runLabel = computed(() =>
  selectedRevisionUids.value.length
    ? `Run on ${selectedRevisionUids.value.length} file${selectedRevisionUids.value.length > 1 ? 's' : ''}`
    : 'Run analysis'
);

const runDisabledReason = computed<string | null>(() => {
  if (!activeAnalysis.value) return 'Add an analysis first.';
  if (!activeAnalysis.value.playbook_uid) return 'This analysis has no playbook yet.';
  if (!selectedRevisionUids.value.length) return 'Select files in the data room to run them.';
  return null;
});

function runActiveAnalysis(): void {
  const analysis = activeAnalysis.value;
  if (!analysis || runDisabledReason.value) return;
  void startRunsForFiles(
    analysis,
    selectedRevisionUids.value.map(uid => ({
      revisionUid: uid,
      fileName: reviewProjectStore.dataRoomFileNameByRevisionUid[uid],
    }))
  );
}

/* --------------------------------------- dropping data-room files on a tab */

function onAnalysisDragOver(analysis: ReviewProjectAnalysisInDb, event: DragEvent): void {
  // No preventDefault without a playbook: the browser then shows not-allowed.
  if (!hasVaultFileDragData(event) || !analysis.playbook_uid) return;
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'copy';
  }
  dragOverUid.value = analysis.uid;
}

function onAnalysisDragLeave(analysis: ReviewProjectAnalysisInDb, event: DragEvent): void {
  // Moving between children of the drop zone also fires dragleave — ignore those.
  const related = event.relatedTarget;
  if (
    related instanceof Node &&
    event.currentTarget instanceof Node &&
    event.currentTarget.contains(related)
  )
    return;
  if (dragOverUid.value === analysis.uid) {
    dragOverUid.value = null;
  }
}

function onAnalysisDrop(analysis: ReviewProjectAnalysisInDb, event: DragEvent): void {
  if (dragOverUid.value === analysis.uid) {
    dragOverUid.value = null;
  }
  const entries = readVaultFileDragData(event);
  if (!entries.length || !analysis.playbook_uid) return;
  event.preventDefault();
  void startRunsForFiles(analysis, entries);
}
</script>

<template>
  <gaia-workspace-pane title="Analysis" :scroll-body="false" pad-body data-tour="rp.analysis">
    <template #icon>
      <n-icon :component="IconGrid" :size="14" />
    </template>

    <template #actions>
      <n-tooltip placement="bottom-end" :delay="300" :disabled="!runDisabledReason">
        <template #trigger>
          <gaia-button
            type="primary"
            size="small"
            :disabled="!!runDisabledReason"
            :loading="running"
            @click="runActiveAnalysis"
          >
            <template #icon>
              <n-icon :component="IconPlay" />
            </template>
            {{ runLabel }}
          </gaia-button>
        </template>
        {{ runDisabledReason }}
      </n-tooltip>
    </template>

    <template #toolbar>
      <n-flex align="center" :size="8">
        <gaia-analysis-tab
          v-for="analysis in analyses"
          :key="analysis.uid"
          :label="analysis.name"
          :active="analysis.uid === reviewProjectStore.analysisTabUid"
          :drag-over="dragOverUid === analysis.uid"
          @select="reviewProjectStore.analysisTabUid = analysis.uid"
          @dblclick="startRename(analysis)"
          @dragover="(event: DragEvent) => onAnalysisDragOver(analysis, event)"
          @dragleave="(event: DragEvent) => onAnalysisDragLeave(analysis, event)"
          @drop="(event: DragEvent) => onAnalysisDrop(analysis, event)"
        >
          <gaia-inline-edit
            v-if="renamingUid === analysis.uid"
            :value="analysis.name"
            size="tiny"
            :style="{ width: '140px' }"
            @commit="commitRename"
            @cancel="renamingUid = null"
          />

          <template #actions>
            <gaia-icon-button
              :dimension="20"
              :title="isInSettings(analysis.uid) ? 'Save settings' : 'Analysis settings'"
              :aria-label="isInSettings(analysis.uid) ? 'Save settings' : 'Analysis settings'"
              @click="handleSettingsToggle(analysis)"
            >
              <n-icon
                :component="isInSettings(analysis.uid) ? IconCheck : IconSettings"
                :size="13"
              />
            </gaia-icon-button>
            <gaia-delete-button size="xs" subject="analysis" @click="confirmDelete(analysis.uid)" />
          </template>
        </gaia-analysis-tab>

        <review-analysis-playbook-picker
          :used-playbook-uids="usedPlaybookUids"
          @pick="addAnalysisFromPlaybook"
        />
      </n-flex>
    </template>

    <gaia-spinner v-if="loading" :style="{ margin: '48px auto' }" />

    <gaia-empty-state v-else-if="!analyses.length" title="No analyses yet">
      An analysis runs one playbook over the data room and lays its evaluations out as columns. Add
      one from a playbook to get started.
    </gaia-empty-state>

    <div
      v-else-if="activeAnalysis"
      class="analysis-body"
      @dragover="(event: DragEvent) => onAnalysisDragOver(activeAnalysis!, event)"
      @dragleave="(event: DragEvent) => onAnalysisDragLeave(activeAnalysis!, event)"
      @drop="(event: DragEvent) => onAnalysisDrop(activeAnalysis!, event)"
    >
      <review-analysis-settings
        v-if="isInSettings(activeAnalysis.uid)"
        :playbook-uid="playbookDrafts[activeAnalysis.uid] ?? null"
        @update:playbook-uid="playbookDrafts[activeAnalysis.uid] = $event"
      />
      <review-analysis-data-table
        v-else-if="activeAnalysis.playbook_uid"
        :key="`${activeAnalysis.uid}:${activeAnalysis.playbook_uid}`"
        :project-uid="props.projectUid"
        :analysis-uid="activeAnalysis.uid"
        :playbook-uid="activeAnalysis.playbook_uid"
        :refresh-token="refreshTokens[activeAnalysis.uid] ?? 0"
      />
      <gaia-empty-state v-else title="No playbook configured">
        Open this analysis's settings and pick the playbook its evaluations come from.
      </gaia-empty-state>
    </div>
  </gaia-workspace-pane>
</template>

<style scoped>
.analysis-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
